import { useState } from 'react';
import { api } from '../api.js';

const DISMISS_KEY = 'aqs.set_password_dismissed';

// Shown on Home once for users who signed in via magic link and don't have
// a password set yet. Dismissible; localStorage so it doesn't nag across
// sessions. When they set one, onDone() re-fetches the session so the
// banner disappears without a page reload.
export function SetPasswordBanner({ hasPassword, onDone, order }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  if (hasPassword || dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {}
    setDismissed(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setErr('Use at least 8 characters.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api('/api/auth/set-password', { method: 'POST', body: { password } });
      setSaved(true);
      setTimeout(() => {
        if (onDone) onDone();
      }, 900);
    } catch (e) {
      setErr(e.message || 'Could not save password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="slide-in span-2"
      style={{
        // Home is an ordered grid; without this the banner defaults to
        // order 0 and squats above the tank's actual state every visit.
        order: typeof order === 'number' ? order : undefined,
        borderRadius: 16,
        padding: 14,
        background: 'var(--accent-bg)',
        border: '1px solid var(--accent-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: 'var(--cyan)',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--cyan-text)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="font-heading"
            style={{
              fontSize: 12,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--cyan)',
              marginBottom: 4,
            }}
          >
            Set a password
          </div>
          <p style={{ fontSize: 15, lineHeight: 1.45, color: 'var(--text)' }}>
            {saved
              ? 'Saved. Next sign-in is instant.'
              : "Next time you sign in, skip the email. Pick a password — we'll hash it and keep magic links as your backup."}
          </p>
        </div>
      </div>

      {!saved &&
        (open ? (
          <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete="new-password"
              minLength={8}
              placeholder="New password · 8+ characters"
              style={{
                width: '100%',
                padding: '10px 12px',
                background: 'var(--surface)',
                color: 'var(--text)',
                border: '1px solid var(--surface-border)',
                borderRadius: 10,
                outline: 'none',
                fontSize: 17,
              }}
            />
            {err && (
              <p role="alert" style={{ fontSize: 14, color: 'var(--alert)' }}>
                {err}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="submit"
                disabled={busy || password.length < 8}
                className="font-heading"
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 10,
                  background: 'var(--cyan)',
                  color: 'var(--cyan-text)',
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  opacity: busy || password.length < 8 ? 0.5 : 1,
                }}
              >
                {busy ? 'Saving…' : 'Save password'}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="font-heading"
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  color: 'var(--text-muted)',
                  border: '1px solid var(--surface-border)',
                  fontSize: 13,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setOpen(true)}
              className="font-heading"
              style={{
                flex: 1,
                padding: '10px 0',
                borderRadius: 10,
                background: 'var(--cyan)',
                color: 'var(--cyan-text)',
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              Set password
            </button>
            <button
              onClick={dismiss}
              className="font-heading"
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                color: 'var(--text-muted)',
                border: '1px solid var(--surface-border)',
                fontSize: 13,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              Not now
            </button>
          </div>
        ))}
    </section>
  );
}
